import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PlatformRatings from './PlatformRatings';
import { localizeArt } from './../localize.js';
import ArtTabs from './ArtTabs';
import ArtThumbnail from './ArtThumbnail';
import { formatYear } from '../utils.js';

const CATEGORY_ENUM = {
  1: "movie",
  2: "videoGame",
  3: "book"
};

const TAB_ORDER = ["movie", "book", "videoGame"]; // Tab order for scrolling

const ArtGrid = ({
  arts,
  language,
  mostRecentArtIDs = [],
  aiReasonedArts = [],
  basedOnRecentLogbookArts = [],
  wentLeft = () => {},
  wentRight = () => {},
  initTab = "movie"
}) => {
  const [activeTab, setActiveTab] = useState(initTab);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const filteredArts = arts.filter(art => CATEGORY_ENUM[art.category] === activeTab);

  useEffect(() => {
    const HORIZONTAL_THRESHOLD = 7; // px before treating as horizontal swipe
  
    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
      }
    };
  
    const handleTouchMove = (e) => {
      if (touchStartX.current === null || touchStartY.current === null) return;
  
      const touchX = e.touches[0].clientX;
      const touchY = e.touches[0].clientY;
      const dx = touchX - touchStartX.current;
      const dy = touchY - touchStartY.current;
  
      // Only block vertical scroll when it's clearly a horizontal swipe
      if (
        Math.abs(dx) > HORIZONTAL_THRESHOLD &&       // moved enough horizontally
        Math.abs(dx) > Math.abs(dy) * 1.5            // mostly horizontal
      ) {
        e.preventDefault();
      }
    };
  
    const handleTouchEnd = (e) => {
      if (touchStartX.current === null || touchStartY.current === null) return;
  
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
  
      const dx = touchEndX - touchStartX.current;
      const dy = touchEndY - touchStartY.current;
  
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        const currentIndex = TAB_ORDER.indexOf(activeTab);
  
        if (dx < 0) { // swipe left → next tab
          if (currentIndex < TAB_ORDER.length - 1) {
            setActiveTab(TAB_ORDER[currentIndex + 1]);
          } else {
            wentRight();
          }
        } else if (dx > 0) { // swipe right → previous tab
          if (currentIndex > 0) {
            setActiveTab(TAB_ORDER[currentIndex - 1]);
          } else {
            wentLeft();
          }
        }
      }
  
      touchStartX.current = null;
      touchStartY.current = null;
    };
  
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      window.addEventListener('touchstart', handleTouchStart, { passive: false });
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd, { passive: false });
    }
  
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [activeTab, wentLeft, wentRight]);

  return (
    <div>
      <ArtTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        language={language}
        movies={arts.filter(art => art.category === 1)}
        books={arts.filter(art => art.category === 3)}
        games={arts.filter(art => art.category === 2)}
      />

      <div className="mt-3">
        {filteredArts.length > 0 ? (
          <div className="grid grid-cols-4 gap-3">
            {filteredArts.map((art) => (
              <div className="mb-2" key={art.id}>
                <Link to={'/arts/' + art.id}>
                  <div className="flex justify-center">
                    <ArtThumbnail art={art} />
                  </div>
                </Link>
                <div class="text-center w-full font-medium text-sm tracking-wide leading-snug text-white">
                  {localizeArt(art, language).name + formatYear(art.year, language)}
                </div>
                <div class="mt-[6px]">
                  <PlatformRatings art={art} />
                </div>
                {mostRecentArtIDs.includes(art.id) && (
                  <div className="flex justify-center text-center text-bold text-green-400 text-xs md:text-sm">
                    <b>{language === "ru" ? "Новое!" : "New!"}</b>
                  </div>
                )}
                {aiReasonedArts.includes(art.id) && (
                  <div className="flex justify-center text-center text-bold text-blue-400 text-xs md:text-sm">
                    <b>{language === "ru" ? "Получено от ИИ с мышлением" : "Based on AI reasoning"}</b>
                  </div>
                )}
                {basedOnRecentLogbookArts.includes(art.id) && (
                  <div className="flex justify-center text-center text-bold text-yellow-400 text-xs md:text-sm">
                    <b>{language === "ru" ? "На основе недавнего из журнала" : "Based on recent logbook"}</b>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex justify-center text-gray-500 mt-4">
            {language === "ru" ? "Здесь пока пусто" : "It's empty here for now"}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtGrid;